<template>
  <div class="consumer-product-detail" v-if="productData">
    <div class="product-info-container">
      <img :src="productData.productThumbnailUrl.originalUrl" />
      <div>
        <h1>{{ productData.productName }}</h1>
        <hr />
        <div>
          <div>
            <p class="info-title grey">정상판매가</p>
            <p class="info-content grey">
              {{ productData.productOriginPrice.toLocaleString() }}원
            </p>
          </div>
          <div>
            <p class="info-title bold">계좌이체가</p>
            <p class="info-content bold">
              {{ productData.productBankSellPrice.toLocaleString() }}원
              <span class="sale-text"
                >🔥{{
                  getDiscountRate(
                    productData.productOriginPrice,
                    productData.productBankSellPrice
                  )
                }}% SALE</span
              >
            </p>
          </div>
          <div>
            <p class="info-title bold">카드결제가</p>
            <p class="info-content bold">
              {{ productData.productSellPrice.toLocaleString() }}원
              <span class="sale-text"
                >🔥{{
                  getDiscountRate(
                    productData.productOriginPrice,
                    productData.productSellPrice
                  )
                }}% SALE</span
              >
            </p>
          </div>
          <div>
            <p class="info-title">배송비</p>
            <p class="info-content">
              <span class="blue bold">50,000원</span>
              이상 결제 시 배송비 무료
            </p>
          </div>
          <hr />
          <div>
            <p class="info-title">옵션</p>
            <select class="info-content" v-model="option">
              <option value="">- [필수] 옵션을 선택해 주세요 -</option>
              <option disabled>--------------------</option>
              <option
                v-for="(item, index) in productData.optionList"
                :key="index"
                :value="item"
              >
                {{ item.optionName }}
              </option>
            </select>
          </div>
          <div
            class="selected-option-container"
            v-if="selectedOptions.length > 0"
          >
            <div v-for="(item, index) in selectedOptions" :key="index">
              <hr />
              <div class="option-container">
                <div class="option-info-container">
                  <div class="option-name-container">
                    <p class="product-name">{{ productData.productName }}</p>
                    <p class="option-name" style="color: #999">
                      - {{ item.optionName }}
                    </p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    max="99"
                    v-model.number="item.count"
                    @input="updateCount(index)"
                  />
                  <button @click="removeOption(index)">
                    <span class="material-icons-outlined">clear</span>
                  </button>
                </div>
                <p>
                  {{
                    (
                      productData.productBankSellPrice * item.count
                    ).toLocaleString()
                  }}원<br />({{
                    (
                      productData.productSellPrice * item.count
                    ).toLocaleString()
                  }}원)
                </p>
              </div>
            </div>
          </div>
          <hr />
          <div>
            <p class="info-title>">
              TOTAL <span class="grey">(QUANTITY)</span>
            </p>
            <p class="info-content" style="text-align: end; font-weight: 700">
              {{ totalBankPrice.toLocaleString() }}원 ({{
                totalCardPrice.toLocaleString()
              }}원)
              <span style="color: #007bff">
                [{{ totalCount.toLocaleString() }}개]
              </span>
            </p>
          </div>
          <div>
            <button class="black" style="flex: 2" @click="buyNow">
              BUY IT NOW
            </button>
            <button class="white" style="flex: 1" @click="addCart">CART</button>
            <button
              class="white"
              style="flex: 1"
              @click="addWishList"
              v-if="!isWishList"
            >
              <span class="material-icons-outlined">favorite_border</span>
            </button>
            <button
              class="white"
              style="flex: 1"
              @click="removeWishList"
              v-else
            >
              <span class="material-icons-outlined">favorite</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <hr />
    <div class="product-detail-container">
      <img
        :src="item.imageOriginUrl"
        v-for="(item, index) in productData.productDetailUrl"
        :key="index"
      />
    </div>
  </div>
</template>

<script setup lang="js">
import { nextTick, onMounted, ref, watch, computed } from "vue";
import { db } from "@/lib/firebase";
import {
  getDoc,
  doc,
  setDoc,
  arrayUnion,
  increment,
  arrayRemove,
  updateDoc,
} from "firebase/firestore";
import { useRoute } from "vue-router";
import router from "@/router";
import { getUserId } from "@/lib/auth";
import { useBusyLock } from "@/lib/useBusyLock";
import { getDiscountRate } from '@/lib/utils';

const { runLocked } = useBusyLock();

const productData = ref(null);
const option = ref("");
const selectedOptions = ref([]);
const userData = ref(null);
const isWishList = ref(false);

const route = useRoute();

const totalCount = computed(() =>
  selectedOptions.value.reduce((sum, item) => sum + item.count, 0)
);

const totalBankPrice = computed(() => {
  if (!productData.value) return 0;
  return selectedOptions.value.reduce(
    (sum, item) => sum + (productData.value.productBankSellPrice || 0) * item.count,
    0
  );
});

const totalCardPrice = computed(() => {
  if (!productData.value) return 0;
  return selectedOptions.value.reduce(
    (sum, item) => sum + (productData.value.productSellPrice || 0) * item.count,
    0
  );
});

// --- 로그인 필요 액션에서 공통으로 사용
function requireLogin() {
  const uid = getUserId();
  if (!uid) {
    alert("로그인 후 이용 가능합니다.");
    router.push("/login");
    return "";
  }
  return uid;
}

// --- userData 기준으로 찜 상태 계산 (DB 재조회 불필요)
function syncWishListState() {
  try {
    if (!userData.value) {
      isWishList.value = false;
      return;
    }
    const wishList = userData.value.userProductWishList || [];
    isWishList.value = wishList.includes(route.query.id);
  } catch (error) {
    console.error("찜 상태 동기화 실패:", error);
  }
}

const buyNow = () =>
  runLocked(async () => {
    const itemsToBuy = selectedOptions.value.map((selected) => ({
      productCode: route.query.id,
      optionName: selected.optionName,
      count: selected.count,
    }));

    if (itemsToBuy.length === 0) {
      alert("구매할 옵션이 없습니다.");
      return;
    }

    router.push({
      path: "/checkout",
      state: {
        orderItems: itemsToBuy,
        query: "now",
      },
    });
  }).catch((error) => {
    console.error("구매 실패:", error);
    alert("구매 실패하였습니다. 다시 시도해주세요.");
  });

const addCart = () =>
  runLocked(async () => {
    if (selectedOptions.value.length === 0) {
      alert("구매할 옵션이 없습니다.");
      return;
    }

    const uid = requireLogin();
    if (!uid) return;

    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      alert("회원 정보를 찾을 수 없습니다.");
      return;
    }

    const user = userSnap.data();
    const cartList = [...(user.userProductCartList || [])];

    // --- 선택 옵션들을 장바구니에 합산 반영
    for (const selected of selectedOptions.value) {
      const existingItem = cartList.find(
        (item) =>
          item.productCode === route.query.id &&
          item.optionName === selected.optionName
      );

      if (existingItem) {
        existingItem.count += selected.count;
        // --- 최대 수량 제한 (원하면 제거 가능)
        if (existingItem.count > 99) existingItem.count = 99;
      } else {
        cartList.push({
          productCode: route.query.id,
          optionName: selected.optionName,
          count: Math.min(Math.max(Number(selected.count) || 1, 1), 99),
        });
      }
    }

    await setDoc(
      userRef,
      {
        userProductCartList: cartList,
      },
      { merge: true }
    );

    // --- 로컬 상태도 초기화
    selectedOptions.value = [];

    if (confirm("장바구니에 상품을 추가했습니다.\n장바구니로 이동하시겠습니까?")) {
      router.push("/cart");
    }
  }).catch((error) => {
    console.error("장바구니 추가 실패:", error);
    alert("장바구니 추가에 실패했습니다.");
  });

const addWishList = () =>
  runLocked(async () => {
    const uid = requireLogin();
    if (!uid) return;

    const productId = route.query.id;
    if (!productId) {
      alert("상품 정보가 올바르지 않습니다.");
      return;
    }

    const userRef = doc(db, "users", uid);
    const productRef = doc(db, "product", productId);

    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      alert("회원 정보를 찾을 수 없습니다.");
      return;
    }

    const currentUser = userSnap.data();
    const wishList = currentUser.userProductWishList || [];

    if (wishList.includes(productId)) {
      alert("이미 찜한 상품입니다.");
      return;
    }

    // --- DB 반영
    await Promise.all([
      setDoc(userRef, { userProductWishList: arrayUnion(productId) }, { merge: true }),
      setDoc(productRef, { productLikeCount: increment(1) }, { merge: true }),
    ]);

    // --- 로컬 상태도 함께 갱신 (reload 불필요)
    userData.value = {
      ...currentUser,
      userProductWishList: [...wishList, productId],
    };
    isWishList.value = true;

    // --- 화면에 좋아요 수 표시 중이라면 로컬 상품 데이터도 반영
    if (productData.value) {
      productData.value.productLikeCount = (productData.value.productLikeCount || 0) + 1;
    }

    alert("찜한 상품으로 추가했습니다.");
  }).catch((error) => {
    console.error("찜 추가 실패:", error);
    alert("찜 추가에 실패했습니다.");
  });

const removeWishList = () =>
  runLocked(async () => {
    const uid = requireLogin();
    if (!uid) return;

    const productId = route.query.id;
    if (!productId) {
      alert("상품 정보가 올바르지 않습니다.");
      return;
    }

    const userRef = doc(db, "users", uid);
    const productRef = doc(db, "product", productId);

    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      alert("회원 정보를 찾을 수 없습니다.");
      return;
    }

    const currentUser = userSnap.data();
    const wishList = currentUser.userProductWishList || [];

    if (!wishList.includes(productId)) {
      alert("이미 찜 취소된 상품입니다.");
      return;
    }

    await Promise.all([
      setDoc(userRef, { userProductWishList: arrayRemove(productId) }, { merge: true }),
      setDoc(productRef, { productLikeCount: increment(-1) }, { merge: true }),
    ]);

    // --- 로컬 상태 갱신 (reload 불필요)
    userData.value = {
      ...currentUser,
      userProductWishList: wishList.filter((id) => id !== productId),
    };
    isWishList.value = false;

    if (productData.value) {
      const nextCount = (productData.value.productLikeCount || 0) - 1;
      productData.value.productLikeCount = nextCount < 0 ? 0 : nextCount;
    }

    alert("상품 찜을 취소하였습니다.");
  }).catch((error) => {
    console.error("찜 취소 실패:", error);
    alert("찜 취소에 실패했습니다.");
  });

function updateCount(index) {
  const updatedItem = selectedOptions.value[index];
  if (!updatedItem) return;

  const count = Number(updatedItem.count);

  // --- 수량은 1 이상 정수만 허용
  if (!Number.isInteger(count) || count <= 0) {
    updatedItem.count = 1;
    return;
  }

  // --- 최대 99개 제한
  if (count > 99) {
    updatedItem.count = 99;
  }
}

function removeOption(index) {
  selectedOptions.value.splice(index, 1);
}

async function fetchProductData() {
  const productId = route.query.id;
  if (!productId) return;

  const productSnap = await getDoc(doc(db, "product", productId));
  if (!productSnap.exists()) {
    alert("상품 정보를 찾을 수 없습니다.");
    return;
  }

  productData.value = {
    id: productSnap.id,
    ...productSnap.data(),
  };
}

async function fetchUserData() {
  const uid = getUserId();
  if (!uid) {
    userData.value = null;
    isWishList.value = false;
    return;
  }

  const userSnap = await getDoc(doc(db, "users", uid));
  userData.value = userSnap.exists() ? userSnap.data() : null;
  syncWishListState();
}

async function incrementViewCount() {
  if (!productData.value?.id) return;

  try {
    await updateDoc(doc(db, "product", productData.value.id), {
      productViewCount: increment(1),
    });

    // --- 로컬 수치도 반영 (UI 표시 시 즉시 반영됨)
    productData.value.productViewCount = (productData.value.productViewCount || 0) + 1;
  } catch (e) {
    console.error("조회수 증가 실패:", e);
  }
}

onMounted(() => {
  runLocked(async () => {
    await fetchProductData();
    await fetchUserData();
    await incrementViewCount();
  }).catch((error) => {
    console.error("초기 데이터 로딩 실패:", error);
    alert("상품 정보를 불러오지 못했습니다.");
  });
});

watch(
  () => option.value,
  async (newVal, oldVal) => {
    if (newVal !== oldVal && newVal !== "") {
      const existingOption = selectedOptions.value.find(
        (item) => item.optionName === newVal.optionName
      );

      if (existingOption) {
        existingOption.count += 1;
        if (existingOption.count > 99) existingOption.count = 99;
      } else {
        selectedOptions.value.push({
          optionName: newVal.optionName,
          count: 1,
        });
      }
    }

    await nextTick();
    option.value = "";
  }
);
</script>

<style lang="scss" scoped>
.consumer-product-detail {
  padding: 36px 24px;
  margin: auto;
  max-width: 1200px;

  > .product-info-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;

    > img {
      width: 100%;
      aspect-ratio: 1 / 1;
      border-radius: 8px;
      object-fit: cover;
      border: 1.5px solid #efefef;
      padding: 4px;
    }

    > div {
      hr {
        margin: 12px 0;
        border: none;
        border-bottom: 1px solid #efefef;
      }

      > div {
        > div {
          padding: 12px 0;
          display: flex;
          align-items: center;
          width: 100%;
          gap: 8px;

          .bold {
            font-weight: 700;
          }

          .grey {
            color: #999;
          }

          .blue {
            color: #007bff;
          }

          > .info-title {
            flex: 1;
            font-size: 16px;
          }

          > .info-content {
            flex: 3;
            font-size: 18px;
            > span.sale-text {
              margin-left: 8px;
              font-size: 14px;
              color: #dc3545;
              font-weight: 500;
            }
          }

          > select {
            height: 44px;
            padding: 0 16px;
            border: 1px solid #ccc;
            border-radius: 4px;
            appearance: none;
            background: url("data:image/svg+xml;charset=UTF-8,%3Csvg fill='black' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")
              no-repeat right 12px center;
            background-size: 24px 24px;
          }

          > button {
            width: 100%;
            height: 48px;
            font-weight: 700;
            font-size: 16px;
            cursor: pointer;
            &.black {
              background-color: black;
              color: white;
            }
            &.white {
              background-color: white;
              color: black;
              border: 1px solid #ccc;
            }
          }
        }

        > .selected-option-container {
          display: block;
          padding: 0;

          > div {
            > .option-container {
              margin-top: 12px;
              display: flex;
              align-items: center;

              > .option-info-container {
                flex: 3;
                display: flex;
                align-items: center;
                gap: 8px;

                > div {
                  > p {
                    font-size: 16px;
                  }

                  > .option-name {
                    margin-top: 4px;
                  }
                }

                input {
                  width: 72px;
                  height: 36px;
                  text-align: center;
                  font-size: 16px;
                  border: 1px solid #ccc;
                  border-radius: 4px;
                }

                > button {
                  padding: 4px;
                  border: none;
                  background: none;
                  cursor: pointer;
                }
              }

              > p {
                font-weight: 700;
                flex: 1;
                text-align: end;
                font-size: 16px;
                line-height: 24px;
              }
            }
          }
        }
      }
    }
  }

  > hr {
    margin: 48px 0;
    border: none;
    border-bottom: 1px solid #efefef;
  }

  > .product-detail-container {
    display: flex;
    flex-direction: column;
    > img {
      width: 100%;
    }
  }
}
</style>
